import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { buildQueryParams } from "../lib/globalUtils";

export const CART_RECOVERY_QUERY_KEY = "cartRecovery";

/** @type {import("@tanstack/react-query").QueryKey} */
export const cartRecoveryRulesQueryKey = (params) => [
  CART_RECOVERY_QUERY_KEY,
  "rules",
  params.page,
  params.pageSize,
  params.searchTerm,
];

/**
 * Where clause: abandoned_cart rules + optional search on name/description
 */
const buildEventRulesWhereClause = (searchTerm = "") => {
  const typeCondition = {
    attribute: "type",
    value: "abandoned_cart",
  };

  if (!searchTerm.trim()) {
    return {
      conditions: [typeCondition],
      conector: "AND",
    };
  }

  const term = searchTerm.trim();

  return {
    conditions: [
      typeCondition,
      {
        conector: "OR",
        conditions: [
          {
            attribute: "name",
            value: {
              condition: "ilike",
              value: `%${term}%`,
            },
          },
          {
            attribute: "description",
            value: {
              condition: "ilike",
              value: `%${term}%`,
            },
          },
        ],
      },
    ],
    conector: "AND",
  };
};

const normalizeListResponse = (response) => {
  const raw = response?.result ?? response;
  const rules = Array.isArray(raw) ? raw : [];
  const pagination = response?.pagination || {};
  return { rules, pagination };
};

const normalizeChannelsResponse = (response) => {
  const raw = response?.result ?? response;
  return Array.isArray(raw) ? raw : [];
};

/**
 * Paginated cart recovery (event) rules
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=10]
 * @param {string} [params.searchTerm=""]
 * @param {boolean} [params.enabled=true]
 */
export const useCartRecoveryRules = (params = {}) => {
  const { page = 1, pageSize = 10, searchTerm = "", enabled = true } = params;

  const whereClause = buildEventRulesWhereClause(searchTerm);

  const queryParams = {
    page,
    page_size: pageSize,
    orderBy: "-id",
    where: JSON.stringify(whereClause),
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: cartRecoveryRulesQueryKey({ page, pageSize, searchTerm }),
    queryFn: async () => {
      const response = await apiClient.get(
        `/event_rules${queryString ? `?${queryString}` : ""}`,
      );
      return normalizeListResponse(response);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Single event rule by id.
 * When `initialData` is provided (from list row), the query is skipped to avoid
 * extra requests if `GET /event_rules/:id` is unavailable.
 * Direct visits without state still attempt the fetch.
 */
export const useCartRecoveryRuleDetail = (ruleId, initialData = null) => {
  const hasInitialRule = Boolean(initialData);

  return useQuery({
    queryKey: [CART_RECOVERY_QUERY_KEY, "detail", ruleId],
    queryFn: async () => {
      const response = await apiClient.get(`/event_rules/${ruleId}`);
      const raw = response?.result ?? response;
      return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
    },
    enabled: Boolean(ruleId) && !hasInitialRule,
    initialData: hasInitialRule ? initialData : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

/**
 * Channels configured for an event rule
 */
export const useCartRecoveryChannels = (ruleId, enabled = true) => {
  return useQuery({
    queryKey: [CART_RECOVERY_QUERY_KEY, "channels", ruleId],
    queryFn: async () => {
      const response = await apiClient.get(`/event_rules/${ruleId}/channels`);
      return normalizeChannelsResponse(response);
    },
    enabled: !!ruleId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

const invalidateCartRecoveryLists = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: [CART_RECOVERY_QUERY_KEY] });
};

/**
 * Create event rule (cart recovery / abandoned_cart)
 */
export const useCreateCartRecoveryRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post("/event_rules", payload);
      return response?.result ?? response;
    },
    onSuccess: () => {
      invalidateCartRecoveryLists(queryClient);
    },
  });
};

/**
 * Update event rule
 */
export const useUpdateCartRecoveryRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, data }) => {
      const response = await apiClient.put(`/event_rules/${ruleId}`, data);
      return response?.result ?? response;
    },
    onSuccess: (_, variables) => {
      invalidateCartRecoveryLists(queryClient);
      queryClient.invalidateQueries({
        queryKey: [CART_RECOVERY_QUERY_KEY, "detail", variables.ruleId],
      });
    },
  });
};

/**
 * Create channel under an event rule
 */
export const useCreateCartRecoveryChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, data }) => {
      const response = await apiClient.post(
        `/event_rules/${ruleId}/channels`,
        data,
      );
      return response?.result ?? response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CART_RECOVERY_QUERY_KEY, "channels", variables.ruleId],
      });
      invalidateCartRecoveryLists(queryClient);
    },
  });
};

/**
 * Update channel
 */
export const useUpdateCartRecoveryChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, channelId, data }) => {
      const response = await apiClient.put(
        `/event_rules/${ruleId}/channels/${channelId}`,
        data,
      );
      return response?.result ?? response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CART_RECOVERY_QUERY_KEY, "channels", variables.ruleId],
      });
      invalidateCartRecoveryLists(queryClient);
    },
  });
};

/**
 * Delete event rule (cart recovery)
 */
export const useDeleteCartRecoveryRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId) => {
      await apiClient.delete(`/event_rules/${ruleId}`);
    },
    onSuccess: (_, ruleId) => {
      invalidateCartRecoveryLists(queryClient);
      queryClient.removeQueries({
        queryKey: [CART_RECOVERY_QUERY_KEY, "detail", ruleId],
      });
      queryClient.removeQueries({
        queryKey: [CART_RECOVERY_QUERY_KEY, "channels", ruleId],
      });
    },
  });
};

/**
 * Delete channel
 */
export const useDeleteCartRecoveryChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, channelId }) => {
      await apiClient.delete(`/event_rules/${ruleId}/channels/${channelId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CART_RECOVERY_QUERY_KEY, "channels", variables.ruleId],
      });
      invalidateCartRecoveryLists(queryClient);
    },
  });
};

export default {
  useCartRecoveryRules,
  useCartRecoveryRuleDetail,
  useCartRecoveryChannels,
  useCreateCartRecoveryRule,
  useUpdateCartRecoveryRule,
  useDeleteCartRecoveryRule,
  useCreateCartRecoveryChannel,
  useUpdateCartRecoveryChannel,
  useDeleteCartRecoveryChannel,
};
